import { motion } from "framer-motion";
import { ArrowUpRight, Phone, FileText, Layers, Briefcase } from "lucide-react";

const networks = [
  {
    tag: "Global Expert Network",
    name: "GLG",
    fullname: "Gerson Lehrman Group",
    note:
      "Verified subject-matter expert — advising institutional investors, private-equity firms, hedge funds and top-tier consultancies on capital projects, cement, steel and industrial operations.",
    accent: "top-left",
  },
  {
    tag: "Expert Advisory Platform",
    name: "Guidepoint",
    fullname: "Guidepoint Global",
    note:
      "On-call industry advisor for institutional clients — expert calls, written commentary and deep-dive project reviews across cement, EPC and heavy manufacturing.",
    accent: "top-right",
  },
];

const engagements = [
  {
    icon: Phone,
    title: "Expert Calls",
    duration: "30 – 60 min",
    body:
      "One-on-one paid consultations for institutional research — market structure, project economics, vendor landscape, unit-level operations.",
  },
  {
    icon: FileText,
    title: "Written Advisory",
    duration: "Task-based",
    body:
      "Structured written response on a specific investment or operating question — with sources, assumptions and confidence bands stated.",
  },
  {
    icon: Layers,
    title: "Deep-Dive Reviews",
    duration: "Multi-week",
    body:
      "Full technical + commercial diligence on a capital project or plant — schedule realism, capex overrun risk, single-vendor dependency, statutory readiness.",
  },
  {
    icon: Briefcase,
    title: "Advisory Retainers",
    duration: "Quarter+",
    body:
      "Ongoing advisor to a management team or investment committee — pre-reads on board packs, project reviews, governance uplift and MIS design.",
  },
];

const sectors = [
  "Cement & building materials",
  "Steel & heavy manufacturing",
  "Power generation · WHRS · thermal",
  "EPC & industrial project delivery",
  "Capital projects · CAPEX governance",
  "Emerging: EV design · cybersecurity for industrial",
];

const row = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function ExpertConsulting() {
  return (
    <section id="expert" className="relative border-b border-surface-2">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">06 —</span><span className="ml-3">Expert Consulting</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              A parallel practice —<br />
              <span className="italic text-brass">expert calls and short-form advisory</span> for institutions.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 text-mutedfg text-sm md:text-base font-light leading-relaxed">
            Alongside board mandates, I take on paid expert engagements through leading global expert
            networks — for funds, consultancies and corporate strategy teams who need practitioner
            realism on industrial capital projects.
          </div>
        </div>

        {/* Network affiliations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-surface-2 border-y border-surface-2">
          {networks.map((n, i) => (
            <motion.div
              key={n.name}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={row}
              className="relative bg-ink p-10 md:p-14 min-h-[280px] flex flex-col justify-between group placard border border-transparent"
              data-testid={`network-${n.name.toLowerCase()}`}
            >
              <span className="absolute left-0 top-0 h-4 w-4 border-l border-t border-brass" />
              <span className="absolute right-0 top-0 h-4 w-4 border-r border-t border-brass" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-6">{n.tag}</div>
                <div className="font-serif-display text-ivory text-5xl md:text-6xl leading-none tracking-tight transition-colors duration-500 group-hover:text-brass">
                  {n.name}
                </div>
                <div className="mt-3 text-mutedfg text-sm uppercase tracking-[0.2em]">{n.fullname}</div>
              </div>
              <p className="mt-8 text-mutedfg leading-relaxed font-light max-w-lg">{n.note}</p>
            </motion.div>
          ))}
        </div>

        {/* Engagement types */}
        <div className="mt-24 mb-16">
          <div className="text-[11px] uppercase tracking-[0.28em] text-brass mb-6">Engagement Types</div>
          <h3 className="font-serif-display text-ivory text-3xl md:text-5xl font-light leading-[1.05] tracking-[-0.01em] max-w-3xl">
            Four ways to engage — from a single expert call to a standing advisory role.
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-surface-2 border-y border-surface-2">
          {engagements.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.div
                key={e.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={row}
                className="bg-ink p-8 md:p-10 min-h-[300px] flex flex-col group placard border border-transparent"
                data-testid={`engagement-${i}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="border border-brass/50 p-3 group-hover:border-brass transition-colors duration-500">
                    <Icon size={18} className="text-brass" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-mutedfg tabular">
                    {e.duration}
                  </span>
                </div>
                <h4 className="font-serif-display text-ivory text-2xl font-light leading-tight transition-colors duration-500 group-hover:text-brass">
                  {e.title}
                </h4>
                <p className="mt-4 text-mutedfg leading-relaxed font-light text-sm flex-1">{e.body}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Sectors of expertise */}
        <div className="mt-20 grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass mb-4">
              Areas of Consulting Expertise
            </div>
            <h3 className="font-serif-display text-ivory text-3xl md:text-4xl font-light leading-tight">
              Twenty-one years of practice, converted into a directly consultable dataset.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="hairline mb-2" />
            <ul>
              {sectors.map((s, i) => (
                <motion.li
                  key={s}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-baseline justify-between py-4 border-b border-surface-2 group"
                >
                  <span className="text-ivory font-light text-base md:text-lg">{s}</span>
                  <span className="tabular text-mutedfg text-xs group-hover:text-brass transition-colors duration-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA band */}
        <div className="mt-16 border border-surface-2 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-2">
              Request an Engagement
            </div>
            <div className="font-serif-display text-ivory text-2xl md:text-3xl font-light leading-tight max-w-2xl">
              Clients can request my time directly, or through{" "}
              <span className="italic text-brass">GLG</span> and{" "}
              <span className="italic text-brass">Guidepoint</span>.
            </div>
          </div>
          <a
            href="#contact"
            data-testid="expert-consulting-cta"
            className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-ink text-[13px] uppercase tracking-[0.2em] font-semibold transition-transform duration-500 hover:-translate-y-[2px] shrink-0"
          >
            Send Project Brief
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
